import { useState } from 'react'
import './AIGenerationModal.css'

const PROMPT_EXAMPLES = [
  {
    category: 'Welcome & Onboarding',
    prompts: [
      {
        label: 'Welcome Series',
        text: 'Create a warm welcome email for new subscribers. Include a friendly greeting with {{first_name}}, introduce 3 key benefits of our service in a columned layout, add a "Get Started" CTA button, and include social media links in the footer. Use a professional yet approachable tone.'
      },
      {
        label: 'Onboarding Steps',
        text: 'Design an onboarding email that guides new users through their first 3 steps. Use a numbered step-by-step layout with icons, include a progress indicator, add a prominent "Complete Setup" button, and a help/support link at the bottom. Keep it concise and action-oriented.'
      }
    ]
  },
  {
    category: 'Sales & Promotions',
    prompts: [
      {
        label: 'Flash Sale',
        text: 'Create an urgent flash sale email with a bold header showing "50% OFF - 24 Hours Only". Include a countdown urgency message, showcase 2 featured products side by side in columns, add a large "Shop Now" CTA button in red, and a promo code section at the bottom. Use high-energy, FOMO-driven copy.'
      },
      {
        label: 'Product Launch',
        text: 'Design a product launch announcement email. Start with an eye-catching header "Introducing [Product Name]", list 3-4 key features in a clean layout, include a product image placeholder, add customer testimonial quote, a "Pre-Order Now" or "Learn More" CTA button, and social sharing links. Build excitement and anticipation.'
      },
      {
        label: 'Abandoned Cart',
        text: 'Create an abandoned cart recovery email. Include a friendly "You left something behind" heading, show the cart items in a table format with product name, quantity and price, add a "Complete Your Purchase" button, include a 10% discount incentive, and customer support contact. Keep tone helpful, not pushy.'
      }
    ]
  },
  {
    category: 'Newsletter & Content',
    prompts: [
      {
        label: 'Weekly Digest',
        text: 'Create a weekly newsletter template with a clean editorial layout. Include a branded header, a featured article section with image placeholder and excerpt, two side-by-side content cards in columns for secondary stories, a "Quick Tips" highlight box, and an unsubscribe footer. Use a professional editorial tone.'
      },
      {
        label: 'Blog Roundup',
        text: 'Design a monthly blog roundup email. Start with "This Month\'s Top Reads" heading, list 4-5 blog posts with title and short description, include "Read More" links for each, add a subscriber-exclusive tip section, and social media follow buttons. Clean, scannable layout.'
      }
    ]
  },
  {
    category: 'Transactional',
    prompts: [
      {
        label: 'Order Confirmation',
        text: 'Create an order confirmation email. Include a green "Order Confirmed!" header with checkmark feel, order details (order number, date, estimated delivery), an itemized table showing products with quantity and price, order total, a "Track Your Order" button, and customer support info at the bottom.'
      },
      {
        label: 'Invoice / Receipt',
        text: 'Design a professional invoice/receipt email. Include company logo placeholder, invoice number and date, billing details in a clean table format with itemized costs, subtotal, tax, and total, payment method info, a "Download PDF" button, and payment terms in the footer.'
      }
    ]
  },
  {
    category: 'Engagement & Events',
    prompts: [
      {
        label: 'Event Invitation',
        text: 'Create an event invitation email with an elegant design. Include an event banner image placeholder, event name as a large heading, date/time/location in a 3-column layout, a compelling description of what attendees will gain, speaker or highlight mentions, a prominent "RSVP Now" button, and an "Add to Calendar" link.'
      },
      {
        label: 'Feedback Request',
        text: 'Design a customer feedback/survey email. Start with "We Value Your Opinion" heading, briefly explain why their feedback matters (1-2 sentences), include a visual 1-5 star rating section or NPS-style prompt, a "Take the Survey" CTA button, mention it takes only 2 minutes, and offer an incentive like a discount code for completing it.'
      },
      {
        label: 'Re-engagement',
        text: 'Create a re-engagement email for inactive users with "We Miss You!" heading. Include a personalized message acknowledging their absence, highlight 2-3 new features or updates they\'ve missed in a column layout, offer a "Welcome Back" incentive or discount, add a "Come Back" CTA button, and an unsubscribe option. Warm, non-guilt-tripping tone.'
      }
    ]
  }
]

export default function AIGenerationModal({ isOpen, onClose, onGenerate }) {
  const [prompt, setPrompt] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [showExamples, setShowExamples] = useState(true)

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError('Please enter your requirements')
      return
    }

    setLoading(true)
    setError(null)

    try {
      await onGenerate(prompt)
      setPrompt('')
      onClose()
    } catch (err) {
      setError(err.message || 'Failed to generate content. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleSelectExample = (text) => {
    setPrompt(text)
    setShowExamples(false)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="ai-modal-overlay" onClick={onClose} onKeyDown={handleKeyDown}>
      <div className="ai-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '720px' }}>
        <div className="ai-modal-header">
          <h2>Generate Email with AI</h2>
          <button className="ai-modal-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        <div className="ai-modal-body" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
          <label htmlFor="ai-prompt" className="ai-modal-label">
            Describe your email requirements
          </label>
          <textarea
            id="ai-prompt"
            className="ai-modal-textarea"
            value={prompt}
            onChange={(e) => {
              setPrompt(e.target.value)
              if (e.target.value.trim()) setShowExamples(false)
            }}
            onFocus={() => { if (!prompt.trim()) setShowExamples(true) }}
            placeholder="Describe the email you want to create... or pick an example below"
            rows={5}
            disabled={loading}
          />

          {error && (
            <div className="ai-modal-error">
              {error}
            </div>
          )}

          {/* Prompt Examples */}
          {showExamples && !loading && (
            <div style={{ marginTop: '16px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '12px'
              }}>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                  Quick Start — Click to use
                </span>
                <button
                  onClick={() => setShowExamples(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '12px',
                    color: '#94a3b8',
                    cursor: 'pointer',
                    padding: '2px 6px'
                  }}
                >
                  Hide
                </button>
              </div>

              {PROMPT_EXAMPLES.map((category) => (
                <div key={category.category} style={{ marginBottom: '12px' }}>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: '700',
                    color: '#94a3b8',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: '6px'
                  }}>
                    {category.category}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {category.prompts.map((example) => (
                      <button
                        key={example.label}
                        onClick={() => handleSelectExample(example.text)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '16px',
                          border: '1px solid #e2e8f0',
                          background: '#f8fafc',
                          color: '#475569',
                          fontSize: '12px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          whiteSpace: 'nowrap'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.background = '#ede9fe'
                          e.currentTarget.style.borderColor = '#c4b5fd'
                          e.currentTarget.style.color = '#6d28d9'
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.background = '#f8fafc'
                          e.currentTarget.style.borderColor = '#e2e8f0'
                          e.currentTarget.style.color = '#475569'
                        }}
                      >
                        {example.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Show examples toggle when hidden */}
          {!showExamples && !loading && (
            <button
              onClick={() => setShowExamples(true)}
              style={{
                marginTop: '10px',
                background: 'none',
                border: 'none',
                fontSize: '12px',
                color: '#6366f1',
                cursor: 'pointer',
                padding: '4px 0',
                fontWeight: '500'
              }}
            >
              Show prompt examples
            </button>
          )}

          <div className="ai-modal-info" style={{ marginTop: '12px' }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
              <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"/>
            </svg>
            <span>AI generates subject line + email blocks. You can edit, rearrange, and customize everything after generation.</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', padding: '8px 12px', background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.15)', borderRadius: '8px', fontSize: '12px', color: '#475569' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
            <span>Template generation uses <strong>2 AI credits</strong> per generation</span>
          </div>
        </div>

        <div className="ai-modal-footer">
          <button
            className="ai-modal-btn ai-modal-btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="ai-modal-btn ai-modal-btn-primary"
            onClick={handleGenerate}
            disabled={loading || !prompt.trim()}
          >
            {loading ? (
              <>
                <span className="ai-modal-spinner"></span>
                Generating...
              </>
            ) : (
              'Generate Email'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
